import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { debounce } from '@/lib/utils';

export interface CompanyData {
  siren: string;
  siret: string;
  denomination: string;
  adresse: string;
  code_postal: string;
  ville: string;
  code_naf: string;
  libelle_naf: string;
  effectif: number;
  suggested_opco: string | null;
  opco_confidence: string | null;
  categorie_juridique?: string | null;
}

interface UseCompanyLookupOptions {
  onCompanyFound?: (data: CompanyData) => void;
  onError?: (error: string) => void;
  debounceMs?: number;
}

interface UseCompanyLookupReturn {
  search: (value: string) => void;
  searchImmediate: (value: string) => Promise<CompanyData | null>;
  companyData: CompanyData | null;
  isLoading: boolean;
  error: string | null;
  isVerified: boolean;
  reset: () => void;
}

export function useCompanyLookup(options: UseCompanyLookupOptions = {}): UseCompanyLookupReturn {
  const { onCompanyFound, onError, debounceMs = 500 } = options;
  
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  
  const abortControllerRef = useRef<AbortController | null>(null);

  const performSearch = useCallback(async (value: string): Promise<CompanyData | null> => {
    // Nettoyer la valeur
    const cleanValue = value.replace(/\s/g, '');
    
    // Valider le format
    const isSiren = /^\d{9}$/.test(cleanValue);
    const isSiret = /^\d{14}$/.test(cleanValue);
    
    if (!isSiren && !isSiret) {
      return null;
    }

    // Annuler la recherche précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('sirene-integration', {
        body: { 
          action: 'search_siren',
          siren: isSiren ? cleanValue : cleanValue.slice(0, 9)
        }
      });

      if (fnError) {
        throw new Error(fnError.message || 'Erreur lors de la recherche');
      }

      if (!data?.success) {
        const errorMsg = data?.error || 'Entreprise non trouvée';
        setError(errorMsg);
        onError?.(errorMsg);
        setIsVerified(false);
        return null;
      }

      const company: CompanyData = {
        siren: data.data.siren,
        siret: data.data.siret,
        denomination: data.data.denomination,
        adresse: data.data.adresse,
        code_postal: data.data.code_postal,
        ville: data.data.ville,
        code_naf: data.data.code_naf,
        libelle_naf: data.data.libelle_naf || data.data.code_naf,
        effectif: data.data.effectif || 0,
        suggested_opco: data.data.suggested_opco || null,
        opco_confidence: null,
        categorie_juridique: data.data.categorie_juridique || null
      };

      setCompanyData(company);
      setIsVerified(true);
      onCompanyFound?.(company);
      return company;

    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      const errorMsg = err instanceof Error ? err.message : 'Erreur de recherche';
      setError(errorMsg);
      onError?.(errorMsg);
      setIsVerified(false);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [onCompanyFound, onError]);

  // Version debounced pour la saisie
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      performSearch(value);
    }, debounceMs),
    [performSearch, debounceMs]
  );

  const search = useCallback((value: string) => {
    const cleanValue = value.replace(/\s/g, '');
    const isSiren = /^\d{9}$/.test(cleanValue);
    const isSiret = /^\d{14}$/.test(cleanValue);
    
    if (isSiren || isSiret) {
      debouncedSearch(value);
    }
  }, [debouncedSearch]);

  const reset = useCallback(() => {
    setCompanyData(null);
    setIsLoading(false);
    setError(null);
    setIsVerified(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    search,
    searchImmediate: performSearch,
    companyData,
    isLoading,
    error,
    isVerified,
    reset
  };
}
